import moment from 'moment';

// Keep in sync with benchling/notebook/reviews/models/review.py
import {User} from 'constants/UserConstants';
import {
  ReviewLifecycleActionType,
  ReviewLifecycleTerminalState,
  ReviewLifecycleType,
  ReviewStatus,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {OptionValue} from 'ui-library/menus/MenuConstants';

import {ReviewableQuery} from './__generated/ReviewableQuery';

export type ReviewableOrInaccessibleGraphQL = ReviewableQuery['reviewable'];

export type ReviewableGraphQL = ExcludeInaccessible<ReviewableOrInaccessibleGraphQL>;

export type ReviewableEntryGraphQL = ExtractByTypename<ReviewableOrInaccessibleGraphQL, 'EntryNode'>;

export type PermissionsGraphQL = ReviewableGraphQL['permissions'];

export type ReviewLifecycleGraphQL = ReviewableGraphQL['parentReviewLifecycles'][number];

export type ReviewGraphQL = NonNullable<ReviewableGraphQL['review']>;

export type ReviewerGraphQL = NonNullable<ReviewGraphQL['reviewers']>[number];

// TODO(TypeScript) - Replace references to the single constants with the ReviewStatus enum.
const REVIEW_SNAPSHOT_IN_PROGRESS = 'REVIEW_SNAPSHOT_IN_PROGRESS';
const REJECTED = 'REJECTED';
const NEEDS_REVIEW = 'NEEDS_REVIEW';
const ACCEPTANCE_SNAPSHOT_IN_PROGRESS = 'ACCEPTANCE_SNAPSHOT_IN_PROGRESS';
const ACCEPTED = 'ACCEPTED';
const RETRACTED = 'RETRACTED';
const CYCLE_COMPLETED = 'CYCLE_COMPLETED';
const ENTRY_IN_PROGRESS = 'IN_PROGRESS';
const ACTION_REQUIRED = 'ACTION_REQUIRED';

// Keep in-sync with benchling/notebook/reviews/review_action.py:ReviewAction
export enum ReviewAction {
  SEND_FOR_REVIEW = 'SEND_FOR_REVIEW',
  ACCEPT = 'ACCEPT',
  REJECT = 'REJECT',
  COMMENT = 'COMMENT',
  RETRACT = 'RETRACT',
  COMPLETE_CYCLE = 'COMPLETE_CYCLE',
}

export enum ReviewBadgeStatus {
  IN_PROGRESS,
  IN_REVIEW,
  ACCEPTED,
  REJECTED,
  ACTION_REQUIRED,
}

export interface ESignatureInfo {
  signatureType: string;
  signature: string;
}

// Not a real review status. Used on the FE to specify a different icon for sent for review items.
export const SENT_FOR_REVIEW = 'SENT_FOR_REVIEW';
export const COMMENT = 'review-comment';
export const IN_PROGRESS_FILTER = ENTRY_IN_PROGRESS;

export interface Snapshot {
  id?: string;
  status: string;
  createdAt: string;
  url?: string | null;
}

export type HistoryItemReviewStatus = Exclude<ReviewStatus, ReviewStatus.ACTION_REQUIRED>;

export interface SignatureData {
  userId: string;
  userFullName: string;
  userHandle: string;
  userEmail: string;
  version: number;
  reviewHistoryUiVersion: number;
  authenticationMethod: string | null | undefined;
  ipAddress: string | null | undefined;
  createdAt: string;
}

export interface HistoryItemInfo {
  id: string;
  reviewAction: ReviewAction;
  new_status: HistoryItemReviewStatus | null;
  message: string | null;
  snapshot?: Snapshot | null;
  wasCreatedBySnapshot: boolean;
  wasEsigned: boolean;
  signatureData?: SignatureData;
  /** Template version this review action applied to, when the backend sends one. */
  reviewItemVersionApiIdentifier?: string | null;
}
interface HistoryItemReviewStageInfo {
  action: ReviewLifecycleActionType;
  name: string;
  definition?: string;
}

export interface HistoryItemReviewLifecycleInfo {
  name?: string;
  reviewStageInfo?: HistoryItemReviewStageInfo;
  terminalState?: ReviewLifecycleTerminalState;
  apiIdentifier?: string;
}

export interface ReviewHistoryExportScope {
  exportedVersionApiId: string;
  /** Publish time of the exported version; later history belongs to subsequent draft work. */
  publishedAtCutoff?: moment.Moment;
}

/**
 * Builds the review history cutoff for a template export.
 *
 * A template export always targets either the current draft or a published version, so the timeline
 * to render is everything that happened up to the moment that version was published. Draft exports
 * have no publishing record and therefore no cutoff.
 *
 * Callers must supply the complete record set: a missing record widens the timeline and leaks
 * later-draft review actions, so failing the export is preferable to scoping against partial data.
 */
export function buildReviewHistoryExportScope(
  exportedVersionApiId: string,
  publishingRecords: ReadonlyArray<{publishableVersionId: string; publishedAt: string}>
): ReviewHistoryExportScope {
  const exportedRecord = publishingRecords.find(
    (record) => record.publishableVersionId === exportedVersionApiId
  );
  return {
    exportedVersionApiId,
    publishedAtCutoff: exportedRecord == null ? undefined : moment(exportedRecord.publishedAt),
  };
}

function isItemIncludedInExport(item: HistoryItem, exportScope: ReviewHistoryExportScope): boolean {
  // Actions on the exported version itself always belong to it, including the cycle completion
  // recorded just after a successful publish.
  if (item.info?.reviewItemVersionApiIdentifier === exportScope.exportedVersionApiId) {
    return true;
  }
  if (exportScope.publishedAtCutoff == null) {
    return true;
  }
  return moment(item.time).isSameOrBefore(exportScope.publishedAtCutoff);
}

/** Filters review history items by snapshot date and exported-version timeline. */
export function isReviewHistoryItem(
  item: HistoryItem,
  upToDate?: moment.Moment,
  exportScope?: ReviewHistoryExportScope
): item is ReviewHistoryItem {
  return (
    item.hasOwnProperty('info') &&
    item.info != null &&
    item.action === 'REVIEW' &&
    (!upToDate || moment(item.time).isSameOrBefore(upToDate)) &&
    (!exportScope || isItemIncludedInExport(item, exportScope))
  );
}

interface BaseHistoryItem {
  user: User;
  time: string;
}

export interface EntryUpdateHistoryItem extends BaseHistoryItem {
  action: 'UPDATE';
  info?: never;
}
export interface ReviewHistoryItem extends BaseHistoryItem {
  action: 'REVIEW';
  info: HistoryItemInfo;
  reviewLifecycleInfo?: HistoryItemReviewLifecycleInfo;
}

export type HistoryItem = EntryUpdateHistoryItem | ReviewHistoryItem;

// A ReviewerUser is just like a user but also contains a boolean on if
// they have approved the review being shown.
export type ReviewerUser = User & {approved: boolean};

export interface ReviewSummaryRLCVersion {
  id: number;
  name: string;
  lifecycleType: ReviewLifecycleType;
  terminalState: ReviewLifecycleTerminalState;
}

export interface ReviewSummary {
  id: string;
  status: ReviewStatus;
  authors: Array<User>;
  auditors: Array<ReviewerUser>;
  reviewLifecycleVersion?: ReviewSummaryRLCVersion;
}

export interface Review extends ReviewSummary {
  history?: Array<HistoryItem>;
}

export interface ReviewChangeTimestamps {
  ACCEPTED: string | null;
  NEEDS_REVIEW: string | null;
  reviewLastAcceptedOn: string | null;
  reviewLastStartedOn: string | null;
  reviewFirstStartedOn: string | null;
}

export type ReviewBadgeDropdownOptionDetails =
  | {
      type: 'header';
      text: string;
    }
  | {
      type: 'option';
      text: string;
      icon: BenchlingIconName;
      onSelect: (value?: OptionValue) => void;
      dataTestAttrs?: DataTestAttrs;
      isSelfReviewAction?: boolean;
    };

export default {
  REVIEW_SNAPSHOT_IN_PROGRESS,
  NEEDS_REVIEW,
  REJECTED,
  ACCEPTANCE_SNAPSHOT_IN_PROGRESS,
  ACCEPTED,
  RETRACTED,
  CYCLE_COMPLETED,
  ACTION_REQUIRED,
  ALL_STATUSES: [
    REVIEW_SNAPSHOT_IN_PROGRESS,
    NEEDS_REVIEW,
    REJECTED,
    ACCEPTANCE_SNAPSHOT_IN_PROGRESS,
    ACCEPTED,
    RETRACTED,
    CYCLE_COMPLETED,
    ACTION_REQUIRED,
  ],
  LOCKED_STATUSES: [
    REVIEW_SNAPSHOT_IN_PROGRESS,
    NEEDS_REVIEW,
    ACCEPTANCE_SNAPSHOT_IN_PROGRESS,
    ACCEPTED,
    ACTION_REQUIRED,
  ],

  SENT_FOR_REVIEW,
  COMMENT,

  // Not a status, but used as a filter.
  IN_PROGRESS_FILTER: ENTRY_IN_PROGRESS,

  ROLE_FILTERS: {
    AUTHOR: 'author',
    AUDITOR: 'auditor',
    AUTHOR_OR_AUDITOR: 'any',
  },
  ROLE_FILTER_SEPARATOR: ',',
};
